import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CONTRACT_KIND_LABELS, CONTRACT_KINDS, CONTRACT_STATUS_LABELS, CONTRACT_STATUSES, type ContractSummary, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { useSession } from '../../session';
import './contracts.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
export const day = (iso: string | null) => (iso ? dateFormat.format(new Date(iso)) : '–');

/** Liste der Verträge mit Suche und Filter nach Stand und Art. Suche, Filter und Seite stehen in der Adresszeile. */
export function ContractsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const status = params.get('stand') ?? '';
  const kind = params.get('art') ?? '';
  const q = params.get('q') ?? '';
  const page = Number(params.get('page') ?? 1);

  const list = useQuery({
    queryKey: ['contracts', { status, kind, q, page }],
    queryFn: () => api<Paginated<ContractSummary>>('GET', `/contracts${queryString({ status, kind, q, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };

  const fields: FilterField[] = [
    { key: 'stand', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...CONTRACT_STATUSES.map((s) => ({ value: s, label: CONTRACT_STATUS_LABELS[s] }))] },
    { key: 'art', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...CONTRACT_KINDS.map((k) => ({ value: k, label: CONTRACT_KIND_LABELS[k] }))] },
  ];

  return (
    <>
      <div className="page-head">
        <h1>Verträge</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('contracts.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/vertraege/neu">Neuer Vertrag</Link>}
      </div>

      <FilterBar
        search={{ value: q, placeholder: 'Titel, Vertragspartner oder Nummer', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ stand: status, art: kind }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Titel</th>
              <th>Vertragspartner</th>
              <th>Stand</th>
              <th>Gesendet</th>
              <th>E-signiert am</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr><td colSpan={6} className="empty">{q || status || kind ? 'Keine Verträge zu dieser Suche.' : 'Noch keine Verträge. Legen Sie den ersten über „Neuer Vertrag“ an.'}</td></tr>
            )}
            {list.data?.data.map((c) => (
              <ExpandRow
                key={c.id}
                summary={
                  <Summary
                    title={<Link to={`/vertraege/${c.id}`} className="strong-link">{c.title}</Link>}
                    line2={`${c.partyName} · ${CONTRACT_STATUS_LABELS[c.status]}`}
                    line3={`${c.number} · ${CONTRACT_KIND_LABELS[c.kind]}`}
                  />
                }
              >
                <td data-label="Nummer" className="nowrap">{c.number}</td>
                <td className="cell-desktop-title"><Link to={`/vertraege/${c.id}`} className="strong-link">{c.title}</Link></td>
                <td data-label="Vertragspartner">{c.partyName}</td>
                <td data-label="Stand"><span className={`badge badge--contract-${c.status}`}>{CONTRACT_STATUS_LABELS[c.status]}</span></td>
                <td data-label="Gesendet">{day(c.sentAt)}</td>
                <td data-label="E-signiert am">{day(c.signedAt)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>

      {list.data && list.data.meta.lastPage > 1 && (
        <nav className="pager" aria-label="Seiten">
          <button className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </nav>
      )}
    </>
  );
}
